/**
 * La langue active, et comment on en change.
 *
 * Un contexte plutôt qu'une variable de module : changer de langue doit **refaire
 * l'écran**, et une variable ne déclenche aucun rendu. C'est le même piège que la v1,
 * où l'état de manche vivait hors de l'état de jeu.
 *
 * ## L'attribut `lang` suit vraiment
 *
 * `appliquerLangueAuDocument` n'est pas de la cosmétique : c'est cet attribut qui
 * décide de la voix d'un lecteur d'écran, de la coupure des mots et du dictionnaire de
 * correction. Une page qui se déclare française et affiche de l'anglais se fait lire
 * avec un accent français — et personne ne s'en aperçoit en regardant l'écran.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from "react";

import {
  appliquerLangueAuDocument, langueDuNavigateur, textesDe,
  type Langue, type Textes,
} from "./index.js";

interface Contexte {
  readonly langue: Langue;
  readonly textes: Textes;
  readonly changerLangue: (langue: Langue) => void;
}

const LangueContexte = createContext<Contexte | null>(null);

export function FournisseurDeLangue({
  depart, onChangement, children,
}: {
  /** La langue retenue, ou `undefined` pour suivre le navigateur. */
  readonly depart?: Langue | undefined;
  /** Prévenu à chaque changement, pour que l'appelant la retienne. */
  readonly onChangement?: ((langue: Langue) => void) | undefined;
  readonly children: ReactNode;
}) {
  const [langue, setLangue] = useState<Langue>(() => depart ?? langueDuNavigateur());

  // Le coffre est asynchrone : la langue retenue arrive après le premier rendu.
  useEffect(() => {
    if (depart !== undefined) setLangue(depart);
  }, [depart]);

  useEffect(() => { appliquerLangueAuDocument(langue); }, [langue]);

  const changerLangue = useCallback((nouvelle: Langue) => {
    setLangue(nouvelle);
    onChangement?.(nouvelle);
  }, [onChangement]);

  const valeur = useMemo<Contexte>(
    () => ({ langue, textes: textesDe(langue), changerLangue }),
    [langue, changerLangue]);

  return (
    <LangueContexte.Provider value={valeur}>{children}</LangueContexte.Provider>
  );
}

/**
 * La langue active et son dictionnaire.
 *
 * Lève hors du fournisseur plutôt que de retomber sur une langue par défaut : un
 * écran qui s'affiche dans la mauvaise langue sans rien dire est exactement le défaut
 * qu'on cherche à rendre impossible.
 */
export function useLangue(): Contexte {
  const contexte = useContext(LangueContexte);
  if (!contexte) throw new Error("useLangue hors de FournisseurDeLangue");
  return contexte;
}

/** Raccourci : le dictionnaire seul, qui est ce dont 90 % des écrans ont besoin. */
export function useTextes(): Textes {
  return useLangue().textes;
}
